export interface PersistedImage {
  id: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
  zIndex: number;
}

export interface PersistedText {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
  zIndex: number;
}

export interface PersistedImageMeta {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
  zIndex: number;
}

export interface PersistedTextMeta {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
  zIndex: number;
}

export interface PersistedState {
  images: PersistedImage[];
  texts: PersistedText[];
  camera: { x: number; y: number; zoom: number };
}

export interface PersistedStateMeta {
  images: PersistedImageMeta[];
  texts: PersistedTextMeta[];
  camera: { x: number; y: number; zoom: number };
}

export type ClientMessage =
  | { type: "state:full"; state: PersistedState }
  | { type: "state:meta"; state: PersistedStateMeta };

export type ServerMessage =
  | { type: "state:full"; state: PersistedState }
  | { type: "state:meta"; state: PersistedStateMeta }
  | { type: "boards:updated" };

// --- internal state ---
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 500;
const MAX_RECONNECT_DELAY = 30_000;
let shouldReconnect = false;
let lastSentStateSignature:
  | {
      imageIds: Set<string>;
      textIds: Set<string>;
      textContentById: Map<string, string>;
    }
  | null = null;

/** Called when the socket reaches OPEN (initial connect or reconnect) to flush local state if a send was skipped earlier. */
let onWebSocketOpen: (() => void) | null = null;

export function setWebSocketOpenHandler(handler: (() => void) | null) {
  onWebSocketOpen = handler;
}

// Two separate callbacks: one for the initial load, one for remote updates
let initialLoadResolve: ((state: PersistedState | null) => void) | null = null;
let remoteCallback: ((msg: ServerMessage) => void) | null = null;
let boardsCallback: (() => void) | null = null;

// After initial load, the server sends state:full on every reconnect.
// We skip the first state:full per connection to avoid overwriting local state.
let awaitingInitialMessage = false;

/** Active canvas for the current WebSocket (set in connectSync). */
let socketCanvasId: string | null = null;

function wsUrl(): string {
  if (!socketCanvasId) throw new Error("connectSync(canvasId) must set socket canvas first");
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const q = encodeURIComponent(socketCanvasId);
  return `${proto}//${location.host}/ws?canvas=${q}`;
}

async function httpLoadState(): Promise<PersistedState | null> {
  if (!socketCanvasId) return null;
  const res = await fetch(`/api/canvases/${encodeURIComponent(socketCanvasId)}`);
  if (!res.ok) return null;
  return res.json();
}

function scheduleReconnect() {
  if (!shouldReconnect) return;
  const jitter = Math.random() * reconnectDelay * 0.3;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openWs();
  }, reconnectDelay + jitter);
  reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
}

function openWs() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  try {
    ws = new WebSocket(wsUrl());
  } catch {
    scheduleReconnect();
    return;
  }

  // The server sends state:full on every open. We need to handle it differently
  // for initial load vs. reconnection.
  awaitingInitialMessage = true;

  ws.onopen = () => {
    onWebSocketOpen?.();
  };

  ws.onmessage = (ev) => {
    void (async () => {
      try {
        const raw = ev.data;
        let text: string;
        if (typeof raw === "string") text = raw;
        else if (raw instanceof Blob) text = await raw.text();
        else if (raw instanceof ArrayBuffer) text = new TextDecoder().decode(raw);
        else if (raw instanceof Uint8Array) text = new TextDecoder().decode(raw);
        else return;

        const msg = JSON.parse(text) as ServerMessage;

        // Board list changes can arrive at any time
        if (msg.type === "boards:updated") {
          boardsCallback?.();
          return;
        }

        // First message on this connection is always the server's initial state:full
        if (awaitingInitialMessage) {
          awaitingInitialMessage = false;

          if (initialLoadResolve && msg.type === "state:full") {
            const resolve = initialLoadResolve;
            initialLoadResolve = null;
            if (fallbackTimer) {
              clearTimeout(fallbackTimer);
              fallbackTimer = null;
            }
            resolve(msg.state);
            return;
          }

          // Reconnection: skip the server's initial state (we already have local state)
          return;
        }

        remoteCallback?.(msg);
      } catch {
        /* ignore bad frames */
      }
    })();
  };

  ws.onclose = () => {
    ws = null;
    lastSentStateSignature = null;
    scheduleReconnect();
  };

  ws.onerror = () => {
    ws?.close();
  };
}

/**
 * Opens a WebSocket connection and waits for the server's initial state:full.
 * Falls back to HTTP GET after 5s timeout.
 */
export function connectSync(canvasId: string): Promise<PersistedState | null> {
  socketCanvasId = canvasId;
  shouldReconnect = true;
  reconnectDelay = 500;

  return new Promise((resolve) => {
    initialLoadResolve = resolve;

    fallbackTimer = setTimeout(() => {
      fallbackTimer = null;
      if (initialLoadResolve) {
        initialLoadResolve = null;
        httpLoadState().then(resolve);
      }
    }, 5_000);

    openWs();
  });
}

export function disconnectSync() {
  shouldReconnect = false;
  initialLoadResolve = null;
  if (fallbackTimer) {
    clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.onmessage = null;
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.close();
    ws = null;
  }
  lastSentStateSignature = null;
}

/**
 * Send state over WebSocket. Automatically picks state:full vs state:meta.
 * state:meta omits dataUrl from images (much smaller for position/camera changes).
 */
export function sendState(state: PersistedState) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;

  const currentImageIds = new Set(state.images.map((img) => img.id));
  const currentTextIds = new Set(state.texts.map((txt) => txt.id));
  const currentTextContentById = new Map(state.texts.map((txt) => [txt.id, txt.text]));

  const imageSetChanged =
    !lastSentStateSignature ||
    currentImageIds.size !== lastSentStateSignature.imageIds.size ||
    [...currentImageIds].some((id) => !lastSentStateSignature?.imageIds.has(id));

  const textSetChanged =
    !lastSentStateSignature ||
    currentTextIds.size !== lastSentStateSignature.textIds.size ||
    [...currentTextIds].some((id) => !lastSentStateSignature?.textIds.has(id));

  const textContentChanged =
    !lastSentStateSignature ||
    [...currentTextContentById].some(
      ([id, text]) => lastSentStateSignature?.textContentById.get(id) !== text
    );

  if (imageSetChanged || textSetChanged || textContentChanged) {
    const msg: ClientMessage = { type: "state:full", state };
    ws.send(JSON.stringify(msg));
    lastSentStateSignature = {
      imageIds: currentImageIds,
      textIds: currentTextIds,
      textContentById: currentTextContentById,
    };
  } else {
    const metaState: PersistedStateMeta = {
      camera: state.camera,
      images: state.images.map(({ id, x, y, width, height, naturalWidth, naturalHeight, zIndex }) => ({
        id, x, y, width, height, naturalWidth, naturalHeight, zIndex,
      })),
      texts: state.texts.map(({ id, x, y, width, height, fontSize, lineHeight, zIndex }) => ({
        id, x, y, width, height, fontSize, lineHeight, zIndex,
      })),
    };
    const msg: ClientMessage = { type: "state:meta", state: metaState };
    ws.send(JSON.stringify(msg));
  }
}

/**
 * Register a callback for incoming remote state updates (cross-device sync).
 */
export function onRemoteState(cb: (msg: ServerMessage) => void) {
  remoteCallback = cb;
}

/**
 * Register a callback for board list changes (create/delete/rename from another tab).
 */
export function onBoardsUpdated(cb: (() => void) | null) {
  boardsCallback = cb;
}
